import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  NbMediaBreakpointsService,
  NbMenuItem,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
  NbDialogService
} from '@nebular/theme';

import { LayoutService } from '../../../@core/utils';
import { map, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { User, UserRole } from '../../../pages/customers/customers.type';
import { AccessManagerComponent } from '../access-manager/access-manager.component';
import { Router } from '@angular/router';
import { NotificationsService } from '../../../pages/notifications/notifications.service';
import { EventsEnum, Notification } from '../../../pages/notifications/notifications.type';
import { NotificationsPaginatedResponseType } from '../../../shared/typings/api-response.type';
import { CustomToastService } from '../../../shared/custom-toast/custom-toast.service';

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  menuClickSubscription: Subscription;
  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly = false;
  user: User;

  themes = [
    { value: 'default', name: 'Light' },
    { value: 'dark', name: 'Dark' },
    { value: 'cosmic', name: 'Cosmic' },
    { value: 'corporate', name: 'Corporate' }
  ];

  currentTheme = 'default';

  isDarkMode = localStorage.getItem('boardTheme') !== 'light';

  userMenu: NbMenuItem[] = this.buildUserMenu();

  private buildUserMenu(): NbMenuItem[] {
    const menu: NbMenuItem[] = [
      {
        title: 'My Payments',
        link: '/pages/employee-payments/my-payments'
      },
      {
        title: 'My Gmail Accounts',
        link: '/pages/gmail-accounts'
      }
    ];

    // Holidays on the Board (staff only, like the Holidays page).
    if (this.user?.role && this.user.role !== UserRole.ENTITY_OWNER) {
      menu.push({ title: 'Have a Break', link: '/pages/holidays' });
    }

    if (
      this.user?.role === UserRole.SUPER_ADMIN ||
      this.user?.role === UserRole.OPERATION_MANAGER
    ) {
      menu.push({
        title: 'Manage access',
        data: { action: () => this.openAccessManager() }
      });
    }

    menu.push(
      {
        title: this.isDarkMode ? '☀️ Switch to Light Mode' : '🌙 Switch to Dark Mode',
        data: { action: () => this.toggleThemeMode() }
      },
      { title: 'Log out', data: { action: () => this.logout() } }
    );

    return menu;
  }

  openAccessManager(): void {
    this.dialogService.open(AccessManagerComponent, {
      closeOnBackdropClick: true,
      closeOnEsc: true
    });
  }

  notifications: Notification[] = [];
  notificationsTotal = 0;
  notificationsPage = 1;
  readonly pageSize = 10;
  loadingNotifications = false;
  unreadCount = 0;
  notificationFilter: 'all' | 'unread' | 'tasks' | 'payments' = 'all';
  private lastSeenNotificationId: number | null = null;
  private smartPollInterval: any = null;
  private reloadInterval: any = null;
  private toastedNotificationIds = new Set<number>();
  private initialLoadComplete = false;

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private router: Router,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
    private customToastService: CustomToastService,
    private dialogService: NbDialogService
  ) {}

  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;
    this.user = localStorage.user ? JSON.parse(localStorage.user) : null;
    this.userMenu = this.buildUserMenu();

    // Apply saved theme preference
    if (localStorage.getItem('boardTheme') === 'light') {
      document.body.classList.add('theme-light');
      document.body.classList.remove('theme-dark');
    } else {
      document.body.classList.add('theme-dark');
      document.body.classList.remove('theme-light');
    }

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$)
      )
      .subscribe((isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl));

    this.themeService
      .onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$)
      )
      .subscribe((themeName) => (this.currentTheme = themeName));

    this.menuClickSubscription = this.menuService.onItemClick().subscribe((event) => {
      if (event.tag === 'user-menu' && event.item?.data?.action) {
        event.item.data.action();
      }
    });

    this.loadNotifications(1, true);

    this.reloadInterval = setInterval(() => this.loadNotifications(1, true), 10 * 60 * 1000);

    // Smart notification poll every 30 seconds
    this.smartPollInterval = setInterval(() => this.checkForNewNotifications(), 30 * 1000);
  }

  ngAfterViewInit() {
    // Use setTimeout to defer sidebar toggle until after change detection completes
    setTimeout(() => {
      this.toggleSidebar();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.menuClickSubscription.unsubscribe();
    if (this.smartPollInterval) clearInterval(this.smartPollInterval);
    if (this.reloadInterval) clearInterval(this.reloadInterval);
  }

  private checkForNewNotifications(): void {
    this.notificationsService
      .getEmployeeNotifications(1, 5)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: NotificationsPaginatedResponseType<Notification>) => {
          if (res.statusCode !== 200 || !res.data?.items?.length) return;

          const items = res.data.items;

          // Update unread count
          if (res.data.unreadCount !== undefined) {
            this.unreadCount = res.data.unreadCount;
            this.cdr.markForCheck();
          }

          // Fallback: if lastSeenNotificationId was never seeded (e.g. user had no
          // prior notifications when the page loaded), seed it now and bail — we cannot
          // distinguish old from new on this first data-bearing poll.
          if (this.lastSeenNotificationId === null) {
            this.lastSeenNotificationId = items[0].id;
            return;
          }

          // Find truly new notifications since last check
          const lastSeen = this.lastSeenNotificationId;
          const newOnes = items.filter((n) => n.id > lastSeen);
          if (!newOnes.length) return;

          this.lastSeenNotificationId = items[0].id;

          const targetEvents = [
            EventsEnum.VISA_REQUEST_ASSIGNED_TO_EMPLOYEE,
            EventsEnum.PAYMENT_REQUIRED,
            EventsEnum.TASK_ASSIGNED_TO_EMPLOYEE,
            EventsEnum.REFUND_REQUEST_PENDING_APPROVAL,
            EventsEnum.REFUND_ACCEPTED_NOTIFY_INITIATOR,
            EventsEnum.VISA_REQUEST_APPROVED_FOR_EMPLOYEE,
            EventsEnum.REFUND_PAID_FOR_EMPLOYEE,
            EventsEnum.VISA_REQUEST_REVIEW_READY,
            EventsEnum.VISA_REQUEST_REVIEW_ASPECT_REJECTED,
            EventsEnum.CLIENT_PORTAL_CONFIRMATION_SUBMITTED,
            EventsEnum.APPLICATION_REVIEW_RESPONDED,
            EventsEnum.EMBASSY_EMAIL_RECEIVED,
            EventsEnum.BUG_REPORT_FIXED,
            EventsEnum.LEAVE_REQUEST_CREATED,
            EventsEnum.LEAVE_REQUEST_DECIDED
          ];

          for (const n of newOnes) {
            if (!targetEvents.includes(n.event)) {
              continue;
            }

            const navigateTo = this.getNotificationLink(n) ?? undefined;
            if (!this.toastedNotificationIds.has(n.id)) {
              this.toastedNotificationIds.add(n.id);
              this.customToastService.show(n.title, n.content || '', navigateTo, () =>
                this.markAsRead(n.id)
              );
            }
          }

          // Refresh the notification list in the header dropdown
          this.loadNotifications(1, true);
          this.cdr.markForCheck();
        }
      });
  }

  toggleThemeMode(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('boardTheme', this.isDarkMode ? 'dark' : 'light');
    if (this.isDarkMode) {
      document.body.classList.add('theme-dark');
      document.body.classList.remove('theme-light');
    } else {
      document.body.classList.add('theme-light');
      document.body.classList.remove('theme-dark');
    }
    // Rebuild menu so toggle label updates
    this.userMenu = this.buildUserMenu();
  }

  logout() {
    localStorage.removeItem('auth_app_token');
    localStorage.removeItem('user');
    this.router.navigateByUrl('/auth/login');
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  markAsRead(notificationId: number) {
    this.notificationsService.toggleNotificationReadStatus(notificationId).subscribe((res) => {
      if (res.data?.unreadCount !== undefined) {
        this.unreadCount = res.data.unreadCount;
      }
      const notification = this.notifications.find((n) => n.id === notificationId);
      if (notification?.currentUserReceiver) {
        notification.currentUserReceiver.isRead = true;
      }
      this.cdr.markForCheck();
    });
  }

  private readonly TASK_EVENTS = [EventsEnum.TASK_ASSIGNED_TO_EMPLOYEE];
  private readonly PAYMENT_EVENTS = [
    EventsEnum.PAYMENT_REQUIRED,
    EventsEnum.REFUND_REQUEST_PENDING_APPROVAL,
    EventsEnum.REFUND_ACCEPTED_NOTIFY_INITIATOR,
    EventsEnum.REFUND_PAID_FOR_EMPLOYEE
  ];

  setFilter(filter: 'all' | 'unread' | 'tasks' | 'payments') {
    this.notificationFilter = filter;
  }

  /** Client-side filter of the already-loaded notifications for the active tab. */
  get filteredNotifications(): Notification[] {
    switch (this.notificationFilter) {
      case 'unread':
        return this.notifications.filter((n) => !this.isNotificationRead(n));
      case 'tasks':
        return this.notifications.filter((n) => this.TASK_EVENTS.includes(n.event));
      case 'payments':
        return this.notifications.filter((n) => this.PAYMENT_EVENTS.includes(n.event));
      default:
        return this.notifications;
    }
  }

  markAllRead() {
    this.notificationsService.markAllRead().subscribe((res) => {
      if (res.data?.unreadCount !== undefined) {
        this.unreadCount = res.data.unreadCount;
      }
      this.notifications.forEach((n) => {
        if (n.currentUserReceiver) n.currentUserReceiver.isRead = true;
      });
      this.cdr.markForCheck();
    });
  }

  /** Icon + Nebular status colour per notification category, for scannability. */
  notificationMeta(event: EventsEnum): { icon: string; status: string } {
    switch (event) {
      case EventsEnum.PAYMENT_REQUIRED:
        return { icon: 'credit-card-outline', status: 'warning' };
      case EventsEnum.REFUND_REQUEST_PENDING_APPROVAL:
        return { icon: 'undo-outline', status: 'warning' };
      case EventsEnum.REFUND_ACCEPTED_NOTIFY_INITIATOR:
      case EventsEnum.REFUND_PAID_FOR_EMPLOYEE:
        return { icon: 'undo-outline', status: 'success' };
      case EventsEnum.VISA_REQUEST_REVIEW_READY:
        return { icon: 'checkmark-circle-2-outline', status: 'success' };
      case EventsEnum.VISA_REQUEST_REVIEW_ASPECT_REJECTED:
        return { icon: 'alert-triangle-outline', status: 'danger' };
      case EventsEnum.VISA_REQUEST_APPROVED_FOR_EMPLOYEE:
        return { icon: 'checkmark-circle-outline', status: 'success' };
      case EventsEnum.TASK_ASSIGNED_TO_EMPLOYEE:
        return { icon: 'checkmark-square-outline', status: 'info' };
      case EventsEnum.VISA_REQUEST_ASSIGNED_TO_EMPLOYEE:
        return { icon: 'briefcase-outline', status: 'info' };
      case EventsEnum.EMBASSY_EMAIL_RECEIVED:
        return { icon: 'email-outline', status: 'primary' };
      case EventsEnum.CLIENT_PORTAL_CONFIRMATION_SUBMITTED:
        return { icon: 'person-done-outline', status: 'info' };
      case EventsEnum.APPLICATION_REVIEW_RESPONDED:
        return { icon: 'message-square-outline', status: 'info' };
      case EventsEnum.VISA_REQUEST_APPOINTMENT_SET:
        return { icon: 'calendar-outline', status: 'info' };
      case EventsEnum.VISA_REQUEST_APPOINTMENT_WITHIN_48_HOURS:
        return { icon: 'clock-outline', status: 'warning' };
      case EventsEnum.PASSPORT_EXPIRY_DATE:
      case EventsEnum.VISA_EXPIRY_DATE:
        return { icon: 'alert-circle-outline', status: 'warning' };
      case EventsEnum.VISA_REQUEST_DS_160_STATUS_PENDING_APPROVAL:
      case EventsEnum.VISA_REQUEST_DS_160_STATUS_CONFIRMED:
      case EventsEnum.VISA_REQUEST_FINAL_DETAILS_SET:
        return { icon: 'file-text-outline', status: 'info' };
      case EventsEnum.HAPPY_BIRTHDAY:
        return { icon: 'gift-outline', status: 'primary' };
      case EventsEnum.NO_REPLY:
        return { icon: 'message-circle-outline', status: 'basic' };
      case EventsEnum.NEWS_ALARM:
      case EventsEnum.PUBLIC_NOTIFICATION:
        return { icon: 'info-outline', status: 'primary' };
      default:
        return { icon: 'bell-outline', status: 'basic' };
    }
  }

  /** Compact relative time for recent items, absolute date for older ones. */
  timeAgo(date: Date | string): string {
    const then = new Date(date).getTime();
    if (isNaN(then)) return '';
    const diff = Date.now() - then;
    const min = 60 * 1000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (diff < min) return 'just now';
    if (diff < hr) return `${Math.floor(diff / min)}m`;
    if (diff < day) return `${Math.floor(diff / hr)}h`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
    return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  get unreadNotificationsCount(): number {
    return this.unreadCount;
  }

  /** Load notifications (replace or append). */
  loadNotifications(page: number, replace: boolean) {
    if (this.loadingNotifications) return;
    this.loadingNotifications = true;
    this.notificationsService.getEmployeeNotifications(page, this.pageSize).subscribe({
      next: (res: NotificationsPaginatedResponseType<Notification>) => {
        if (res.statusCode === 200 && res.data) {
          if (replace) {
            this.notifications = res.data.items || [];
            this.notificationsPage = 1;
            // Seed lastSeenNotificationId on the very first load so the poller
            // only surfaces notifications that arrive AFTER the page opened.
            // Use 0 when the employee has no prior notifications so that every
            // future notification (ID >= 1) is correctly treated as new.
            if (this.lastSeenNotificationId === null) {
              this.lastSeenNotificationId = this.notifications.length
                ? this.notifications[0].id
                : 0;
            }
            // On the very first load, toast any recent unread notification
            // so employees who open the board after a notification arrived still see the alert.
            if (!this.initialLoadComplete) {
              this.initialLoadComplete = true;
              const targetEvents = [
                EventsEnum.VISA_REQUEST_ASSIGNED_TO_EMPLOYEE,
                EventsEnum.PAYMENT_REQUIRED,
                EventsEnum.TASK_ASSIGNED_TO_EMPLOYEE,
                EventsEnum.REFUND_REQUEST_PENDING_APPROVAL,
                EventsEnum.REFUND_ACCEPTED_NOTIFY_INITIATOR,
                EventsEnum.VISA_REQUEST_APPROVED_FOR_EMPLOYEE,
                EventsEnum.REFUND_PAID_FOR_EMPLOYEE,
                EventsEnum.VISA_REQUEST_REVIEW_READY,
                EventsEnum.VISA_REQUEST_REVIEW_ASPECT_REJECTED,
                EventsEnum.CLIENT_PORTAL_CONFIRMATION_SUBMITTED,
                EventsEnum.APPLICATION_REVIEW_RESPONDED,
                EventsEnum.EMBASSY_EMAIL_RECEIVED,
                EventsEnum.BUG_REPORT_FIXED,
                EventsEnum.LEAVE_REQUEST_CREATED,
                EventsEnum.LEAVE_REQUEST_DECIDED
              ];
              const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
              // Only toast the most recent matching unread notification (notifications[0] is newest)
              const recent = this.notifications.find(
                (n) =>
                  targetEvents.includes(n.event) &&
                  !this.isNotificationRead(n) &&
                  new Date(n.createdAt) > oneHourAgo
              );
              if (recent && !this.toastedNotificationIds.has(recent.id)) {
                this.toastedNotificationIds.add(recent.id);
                const recentNavigateTo = this.getNotificationLink(recent) ?? undefined;
                this.customToastService.show(
                  recent.title,
                  recent.content || '',
                  recentNavigateTo,
                  () => this.markAsRead(recent.id)
                );
              }
            }
          } else {
            this.notifications = [...this.notifications, ...(res.data.items || [])];
          }
          this.notificationsTotal = res.data.total ?? 0;
          if (res.data.unreadCount !== undefined) {
            this.unreadCount = res.data.unreadCount;
          }
          this.notificationsPage = page;
        }
        this.loadingNotifications = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingNotifications = false;
        this.cdr.markForCheck();
      }
    });
  }

  get hasMoreNotifications(): boolean {
    return this.notifications.length < this.notificationsTotal;
  }

  onNotificationListScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (!el || this.loadingNotifications || !this.hasMoreNotifications) return;
    const threshold = 80;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadNotifications(this.notificationsPage + 1, false);
    }
  }

  isNotificationRead(notification: Notification): boolean {
    return notification.currentUserReceiver?.isRead;
  }

  getNotificationLink(notification: {
    id?: number;
    event?: EventsEnum;
    parentDetails?: { parentId: number; parentEntity: string };
  }): string | null {
    // Sunday "Your week ahead" reminder opens the dashboard, where My day is.
    if (notification?.event === EventsEnum.STAFF_WEEKLY_REMINDER) return '/pages/home';
    const pd = notification?.parentDetails;
    if (!pd?.parentEntity) return null;
    if (pd.parentEntity === 'VisaRequest') {
      return pd.parentId ? `/pages/visa-requests/${pd.parentId}` : null;
    }
    if (pd.parentEntity === 'AssignedTask') {
      return '/pages/tasks-board';
    }
    if (pd.parentEntity === 'LeaveRequest') {
      // Approvers land on the requests to decide; the employee on their holidays.
      // The notification number makes each link a new address, so the page
      // reloads its lists even when it is already open.
      const n = notification?.id ? `n=${notification.id}` : '';
      return notification?.event === EventsEnum.LEAVE_REQUEST_CREATED
        ? `/pages/holidays?tab=requests${n ? `&${n}` : ''}`
        : `/pages/holidays?tab=mine${n ? `&${n}` : ''}`;
    }
    if (pd.parentEntity === 'BugReport') {
      return pd.parentId ? `/pages/bug-reports?bugReportId=${pd.parentId}` : '/pages/bug-reports';
    }
    return null;
  }

  onNotificationClick(notification: Notification) {
    if (!this.isNotificationRead(notification)) {
      this.markAsRead(notification.id);
    }
    const link = this.getNotificationLink(notification);
    if (link) {
      this.router.navigateByUrl(link);
    }
  }
}
